<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  AtomMoney,
  AtomInput,
  AtomInputMoney,
  SatSelect,
  SatButton,
  SatFormControl,
} from "@chatfood/design-system";
import { isGiftCardRepoError } from "@chatfood/dbe-client/errors";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useGiftCardStore } from "~/stores/giftCard";
import { useDirectBackend } from "#imports";
import type { IDomainMoney } from "~/domain/money";
import type { IDomainGiftCardProvider } from "~/domain/gift-card-provider";
import { useServiceError } from "~/composables/use-service-error";

type GiftCardBalance = {
  balance: number;
  giftCardNumber: string;
};

const emits = defineEmits<{ close: [] }>();

const css = bemBuilder("apply-gift-card");

const { t, locale } = useLocale();
const basket = useBasketStore();
const giftCardStore = useGiftCardStore();
const { giftCardRepo } = useDirectBackend();
const { handleBusinessRuleError } = useServiceError();

const step = ref<1 | 2>(1);
const isVerifyingGiftCard = ref(false);
const isApplyingGiftCard = ref(false);
const errorMessage = ref("");
const giftCardBalance = ref<GiftCardBalance | undefined>();

const giftCardCode = ref("");
const giftCardPin = ref<string | undefined>(undefined);
const amountInput = ref<IDomainMoney | undefined>(undefined);
const selectedProvider = ref<IDomainGiftCardProvider>({
  id: "",
  name: "",
  features: { supportsVerificationCode: true },
});

const showPin = computed(
  () =>
    selectedProvider.value.id !== "" &&
    selectedProvider.value.features.supportsVerificationCode,
);

const onSelectProvider = (selected: { id: string; name: string }) => {
  const provider = giftCardStore.providers.find((p) => p.id === selected.id);
  if (provider) {
    selectedProvider.value = provider;
    giftCardPin.value = undefined;
  }
};

watch(
  () => giftCardStore.providers,
  (providers) => {
    if (providers.length === 1) {
      selectedProvider.value = providers[0];
    }
  },
  { immediate: true },
);

const verify = async () => {
  errorMessage.value = "";
  isVerifyingGiftCard.value = true;

  try {
    const balance = await giftCardRepo.getGiftCardBalance({
      outletId: basket.outletId,
      salesChannel: basket.salesChannel,
      providerId: selectedProvider.value.id,
      cardNumber: giftCardCode.value,
      ...(showPin.value && giftCardPin.value ? { pin: giftCardPin.value } : {}),
    });

    giftCardBalance.value = balance;
    step.value = 2;
  } catch (error: unknown) {
    if (isGiftCardRepoError(error) && error.isBusinessRule()) {
      handleBusinessRuleError(error, { via: "toast" });
      return;
    }
    errorMessage.value = t("components.apply_gift_card.error_verify_message");
  } finally {
    isVerifyingGiftCard.value = false;
  }
};

const apply = async () => {
  errorMessage.value = "";
  isApplyingGiftCard.value = true;

  if (!amountInput.value || amountInput.value.value <= 0) {
    errorMessage.value = t("components.apply_gift_card.error_amount_message");
    isApplyingGiftCard.value = false;
    return;
  }

  try {
    await basket.applyGiftCard({
      cardNumber: giftCardCode.value,
      amount: amountInput.value.value,
      pin: showPin.value ? giftCardPin.value || undefined : undefined,
      providerId: selectedProvider.value.id,
    });

    emits("close");
  } catch {
    errorMessage.value = t("components.apply_gift_card.error_message");
  } finally {
    isApplyingGiftCard.value = false;
  }
};
</script>

<template>
  <div :class="css()">
    <AtomText type="h3" color="color-text-headline">
      {{ t("components.apply_gift_card.title") }}
    </AtomText>
    <div :class="css('form')">
      <SatSelect
        v-if="giftCardStore.providers.length > 1"
        :options="giftCardStore.providers"
        size="large"
        data-test="provider-selector"
        :placeholder="
          t('components.apply_gift_card.select_provider_placeholder')
        "
        :has-errors="false"
        :disabled="step === 2"
        :append-to-body="true"
        :value="selectedProvider"
        @select="onSelectProvider"
      />
      <div :class="css('card-fields')">
        <SatFormControl>
          <AtomInput
            :value="giftCardCode"
            :disabled="step === 2"
            data-test="code"
            :placeholder="t('components.apply_gift_card.code_placeholder')"
            @keyup="(value) => (giftCardCode = value)"
          />
        </SatFormControl>
        <SatFormControl v-if="showPin">
          <AtomInput
            data-test="pin"
            :value="giftCardPin"
            :disabled="step === 2"
            :placeholder="t('components.apply_gift_card.pin_placeholder')"
            @keyup="(value) => (giftCardPin = value)"
          />
        </SatFormControl>
      </div>

      <template v-if="step === 2 && giftCardBalance">
        <div :class="css('divider')" />
        <div :class="css('balance-row')">
          <AtomText type="small" color="color-text-secondary">
            {{ t("components.apply_gift_card.wallet_balance") }}
          </AtomText>
          <AtomMoney
            :amount="giftCardBalance.balance"
            :currency="basket.currency"
            :locale="locale"
            :class="css('balance-amount')"
            data-test="wallet-balance"
          />
        </div>
        <div :class="css('balance-row')">
          <AtomText type="small" color="color-text-secondary">
            {{ t("components.apply_gift_card.basket_total") }}
          </AtomText>
          <AtomMoney
            :amount="basket.total.value"
            :currency="basket.total.currency"
            :locale="locale"
            data-test="basket-total"
          />
        </div>
        <SatFormControl :class="css('form-control')">
          <AtomInputMoney
            :has-error="false"
            :disabled="false"
            :value="amountInput ?? { value: 0, currency: basket.currency }"
            data-test="amount"
            @input="(value) => (amountInput = value)"
          />
        </SatFormControl>
        <AtomText
          type="small"
          color="color-text-secondary"
          data-test="amount-hint"
        >
          {{ t("components.apply_gift_card.amount_hint") }}
        </AtomText>
      </template>

      <AtomText
        v-if="errorMessage"
        color="color-error"
        data-test="error-message"
      >
        {{ errorMessage }}
      </AtomText>
    </div>

    <SatButton
      v-if="step === 1"
      size="large"
      theme="filled"
      variant="primary"
      data-test="verify-cta"
      :is-loading="isVerifyingGiftCard"
      :is-disabled="
        !giftCardCode ||
        (giftCardStore.providers.length > 1 && !selectedProvider.id)
      "
      @click.prevent="verify"
    >
      {{ t("components.apply_gift_card.verify_cta") }}
    </SatButton>
    <SatButton
      v-else
      size="large"
      theme="filled"
      variant="primary"
      data-test="apply-cta"
      :is-loading="isApplyingGiftCard"
      :is-disabled="!amountInput?.value"
      @click.prevent="apply"
    >
      {{ t("components.apply_gift_card.apply_cta") }}
    </SatButton>
  </div>
</template>

<style lang="scss" scoped>
.apply-gift-card {
  display: flex;
  gap: var(--spacing-lg);
  flex-direction: column;

  &__form {
    display: flex;
    gap: var(--spacing-md);
    flex-direction: column;
  }

  &__card-fields {
    display: flex;
    gap: var(--spacing-md);

    > * {
      flex: 1;
    }
  }

  &__form-control {
    width: 100%;
  }

  &__divider {
    border-top: 1px solid var(--color-base-90);
  }

  &__balance-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__balance-amount {
    color: var(--color-mint);
  }
}
</style>
